import React, { useMemo } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReadOnlyCardList from './ReadOnlyCardList';
import { calculateEGI } from "../utils/egi";
import { getCommonAreaSquareFeet, getTotalSquareFeetForExpense } from '../utils/operatingExpenseCalc';
import { colors } from '../theme';

interface OperatingExpense {
  id: string;
  name: string;
  factor: number;
  cost_per: string;
  broker?: number;
  statistic?: number | string | null;
}

const getRetailIncome = (unitsArr: any[]) => {
  return unitsArr.reduce((sum: number, u: any) => sum + (u.square_feet * u.rent_per_square_foot_per_year || 0), 0);
};

const OperatingExpensesReadOnly: React.FC<{
  operatingExpenses: OperatingExpense[];
  units: any[];
  amenityIncome: any[];
  modelDetails: any;
  retailIncome?: any[];
  retailExpenses?: any[];
  isDevelopmentModel?: boolean;
  developmentUnits?: any[];
}> = ({ operatingExpenses, units, amenityIncome, modelDetails, retailIncome = [], retailExpenses = [], isDevelopmentModel, developmentUnits = [] }) => {

  const perUnitCount = useMemo(() => {
    if (isDevelopmentModel === true && Array.isArray(developmentUnits) && developmentUnits.length > 0) {
      const total = developmentUnits.reduce((sum: number, du: any) => {
        const n = Number(du?.units ?? 0);
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0);
      return Number.isFinite(total) ? total : 0;
    }
    return Number.isFinite(units?.length) ? (units?.length ?? 0) : 0;
  }, [isDevelopmentModel, developmentUnits, units]);

  const flexByField: Record<string, number> = {
    name: 1.4,
    cost_per: 1,
    factor: 1,
    statistic: 1,
    monthly: 1,
    annual: 1,
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', editable: false, flex: flexByField.name, minWidth: 150, align: 'left', headerAlign: 'left' },
    {
      field: 'cost_per',
      headerName: 'Cost per',
      flex: flexByField.cost_per,
      minWidth: 130,
      editable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const value = String(params.value || '');
        return (
          <span style={{ color: colors.grey[600] }}>{value}</span>
        );
      }
    },
    {
      field: 'factor',
      headerName: 'Expense',
      flex: flexByField.factor,
      minWidth: 130,
      editable: false,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const byUnit = String(params.row.cost_per || '').toLowerCase();
        let prefix = '';
        let suffix = '';
        if (byUnit === 'per unit' || byUnit === 'total') {
          prefix = '$';
        } else if (byUnit === 'percent of egi') {
          suffix = '%';
        } else if (byUnit === 'per ca square foot' || byUnit === 'per total square feet') {
          prefix = '$';
          suffix = '/sf';
        }
        // Ensure value is rounded to nearest integer and comma separated
        let value = params.value;
        if (typeof value === 'number') {
          value = Math.round(value).toLocaleString();
        } else if (typeof value === 'string' && !isNaN(Number(value))) {
          value = Math.round(Number(value)).toLocaleString();
        }
        return (
          <span style={{ color: colors.grey[600] }}>
            {prefix}{value}{suffix}
          </span>
        );
      }
    },
    {
      field: 'statistic',
      headerName: 'Statistic',
      flex: flexByField.statistic,
      minWidth: 120,
      editable: false,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const row = params.row as OperatingExpense;
        let value: number | string | null = null;
        let adornment = '';

        const byUnit = String(row.cost_per || '').toLowerCase();
        if (byUnit === 'per unit') {
          value = perUnitCount;
          adornment = ' units';
        } else if (byUnit === 'total') {
          value = null;
          adornment = '';
        } else if (byUnit === 'per ca square foot') {
          value = getCommonAreaSquareFeet(modelDetails, units, developmentUnits, retailIncome, isDevelopmentModel);
          value = typeof value === 'number' ? value.toLocaleString() : value;
          adornment = ' sf';
        } else if (byUnit === 'per total square feet') {
          const totalSf = getTotalSquareFeetForExpense(modelDetails, isDevelopmentModel, developmentUnits);
          value = totalSf.toLocaleString();
          adornment = ' sf';
        } else if (byUnit === 'percent of egi') {
          const totalRetailIncome = getRetailIncome(retailIncome);
          const getTotalAnnualAmenityIncome = (amenityIncomeArr: any[]) => {
            return amenityIncomeArr.reduce((sum, r) => {
              const usage = Math.round((r.utilization || 0) / 100 * (r.unit_count || 0));
              const monthly = usage * (r.monthly_fee || 0);
              return sum + monthly * 12;
            }, 0);
          };
          let rawValue = 0;
          if (isDevelopmentModel && Array.isArray(developmentUnits) && developmentUnits.length > 0) {
            const devAnnualRental = developmentUnits.reduce((sum: number, du: any) => {
              const unitsCount = Number(du?.units || 0);
              const avgRentMonthly = Number(du?.avg_rent || 0);
              return sum + (Number.isFinite(unitsCount) && Number.isFinite(avgRentMonthly) ? unitsCount * avgRentMonthly * 12 : 0);
            }, 0);
            rawValue = devAnnualRental + getTotalAnnualAmenityIncome(amenityIncome) + totalRetailIncome;
          } else {
            rawValue = calculateEGI({
              modelDetails,
              units,
              amenityIncome,
              retailIncome,
              retailExpenses,
              totalRetailIncome,
            });
          }
          value = rawValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
          adornment = '$ ';
        }

        return (
          <span style={{ color: colors.grey[600] }}>
            {value !== null && value !== undefined
              ? (byUnit === 'percent of egi' ? `${adornment}${value}` : `${value}${adornment}`)
              : ''}
          </span>
        );
      },
    },
    {
      field: 'monthly',
      headerName: 'Monthly',
      flex: flexByField.monthly,
      minWidth: 110,
      editable: false,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const row = params.row as any;
        let value: number | string = 0;
        const byUnit = String(row.cost_per || '').toLowerCase();
        if (byUnit === 'per unit') {
          value = Math.round((row.factor * perUnitCount / 12) * 100) / 100;
        } else if (byUnit === 'total') {
          value = Math.round((row.factor / 12) * 100) / 100;
        } else if (byUnit === 'per ca square foot') {
          const commonAreaSf = getCommonAreaSquareFeet(modelDetails, units, developmentUnits, retailIncome, isDevelopmentModel);
          value = Math.round((row.factor * commonAreaSf / 12) * 100) / 100;
        } else if (byUnit === 'per total square feet') {
          const totalSf = getTotalSquareFeetForExpense(modelDetails, isDevelopmentModel, developmentUnits);
          value = Math.round((row.factor * totalSf / 12) * 100) / 100;
        } else if (byUnit === 'percent of egi') {
          const totalRetailIncome = getRetailIncome(retailIncome);
          const getTotalAnnualAmenityIncome = (arr: any[]) => arr.reduce((sum, r) => sum + Math.round((r.utilization || 0) / 100 * (r.unit_count || 0)) * (r.monthly_fee || 0) * 12, 0);
          let egi = 0;
          if (isDevelopmentModel && Array.isArray(developmentUnits) && developmentUnits.length > 0) {
            const devAnnualRental = developmentUnits.reduce((sum: number, du: any) => sum + (Number(du?.units || 0) * Number(du?.avg_rent || 0) * 12), 0);
            egi = devAnnualRental + getTotalAnnualAmenityIncome(amenityIncome) + totalRetailIncome;
          } else {
            egi = calculateEGI({ modelDetails, units, amenityIncome, retailIncome, retailExpenses, totalRetailIncome });
          }
          value = ((row.factor * egi / 100) / 12 || 0);
        }
        value = (value as number).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

        return (
          <span style={{ color: colors.grey[600] }}>${value}</span>
        );
      },
    },
    {
      field: 'annual',
      headerName: 'Annual',
      flex: flexByField.annual,
      minWidth: 110,
      editable: false,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const row = params.row as any;
        let value: number | string = 0;
        const byUnit = String(row.cost_per || '').toLowerCase();
        if (byUnit === 'per unit') {
          value = row.factor * perUnitCount;
        } else if (byUnit === 'total') {
          value = row.factor;
        } else if (byUnit === 'per ca square foot') {
          const commonAreaSf = getCommonAreaSquareFeet(modelDetails, units, developmentUnits, retailIncome, isDevelopmentModel);
          value = Math.round((row.factor * commonAreaSf));
        } else if (byUnit === 'per total square feet') {
          const totalSf = getTotalSquareFeetForExpense(modelDetails, isDevelopmentModel, developmentUnits);
          value = Math.round((row.factor * totalSf));
        } else if (byUnit === 'percent of egi') {
          const totalRetailIncome = getRetailIncome(retailIncome);
          const getTotalAnnualAmenityIncome = (arr: any[]) => arr.reduce((sum, r) => sum + Math.round((r.utilization || 0) / 100 * (r.unit_count || 0)) * (r.monthly_fee || 0) * 12, 0);
          let egi = 0;
          if (isDevelopmentModel && Array.isArray(developmentUnits) && developmentUnits.length > 0) {
            const devAnnualRental = developmentUnits.reduce((sum: number, du: any) => sum + (Number(du?.units || 0) * Number(du?.avg_rent || 0) * 12), 0);
            egi = devAnnualRental + getTotalAnnualAmenityIncome(amenityIncome) + totalRetailIncome;
          } else {
            egi = calculateEGI({ modelDetails, units, amenityIncome, retailIncome, retailExpenses, totalRetailIncome });
          }
          value = ((row.factor * egi / 100) || 0);
        }
        value = (value as number).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

        return (
          <span style={{ color: colors.grey[600] }}>${value}</span>
        );
      },
    },
  ];

  const CustomFooter = () => {
    const getTotalAnnualAmenityIncome = (arr: any[]) => arr.reduce((sum, r) => sum + Math.round((r.utilization || 0) / 100 * (r.unit_count || 0)) * (r.monthly_fee || 0) * 12, 0);
    const totalRetailIncome = getRetailIncome(retailIncome);

    const computedRows = operatingExpenses.map((row) => {
      let monthly = 0;
      let annual = 0;
      const byUnit = String(row.cost_per || '').toLowerCase();
      if (byUnit === 'per unit') {
        monthly = Math.round((row.factor * perUnitCount / 12) * 100) / 100;
        annual = row.factor * perUnitCount;
      } else if (byUnit === 'total') {
        monthly = Math.round((row.factor / 12) * 100) / 100;
        annual = row.factor;
      } else if (byUnit === 'per ca square foot') {
        const commonAreaSf = getCommonAreaSquareFeet(modelDetails, units, developmentUnits, retailIncome, isDevelopmentModel);
        monthly = Math.round((row.factor * commonAreaSf / 12) * 100) / 100;
        annual = Math.round((row.factor * commonAreaSf));
      } else if (byUnit === 'percent of egi') {
        let egi = 0;
        if (isDevelopmentModel && Array.isArray(developmentUnits) && developmentUnits.length > 0) {
          const devAnnualRental = developmentUnits.reduce((sum: number, du: any) => sum + (Number(du?.units || 0) * Number(du?.avg_rent || 0) * 12), 0);
          egi = devAnnualRental + getTotalAnnualAmenityIncome(amenityIncome) + totalRetailIncome;
        } else {
          egi = calculateEGI({ modelDetails, units, amenityIncome, retailIncome, retailExpenses, totalRetailIncome });
        }
        annual = row.factor * egi / 100;
        monthly = annual / 12;
      } else if (byUnit === 'per total square feet') {
        const totalSf = getTotalSquareFeetForExpense(modelDetails, isDevelopmentModel, developmentUnits);
        monthly = Math.round((row.factor * totalSf / 12) * 100) / 100;
        annual = Math.round((row.factor * totalSf));
      }
      return { ...row, monthly, annual };
    });

    const totalMonthly = computedRows.reduce((sum, row: any) => sum + (row.monthly || 0), 0);
    const totalAnnual = computedRows.reduce((sum, row: any) => sum + (row.annual || 0), 0);

    return (
      <div style={{ 
        padding: '8px 16px',
        backgroundColor: colors.grey[50],
        borderTop: `1px solid ${colors.grey[300]}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', gap: '24px', justifyContent: 'flex-end', width: '100%' }}>
          <div style={{ textAlign: 'right' }}>
            <strong>Total Monthly Expenses:</strong> ${totalMonthly.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </div>
          <div style={{ textAlign: 'right' }}>
            <strong>Total Annual Expenses:</strong> ${totalAnnual.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </div>
        </div>
      </div>
    );
  };

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  if (isMobile) {
    return (
      <Box>
        <ReadOnlyCardList columns={columns} rows={operatingExpenses} titleField="name" getRowId={(r) => r.id} />
        <Box sx={{ mt: 1.5 }}><CustomFooter /></Box>
      </Box>
    );
  }

  return (
    <div>

      <DataGrid
        disableColumnMenu
        disableColumnFilter
        disableColumnSelector
        disableColumnSorting
        rows={operatingExpenses}
        columns={columns}
        disableRowSelectionOnClick
        slots={{ footer: CustomFooter }}
        getRowId={(row) => row.id}
        density="compact"
        sx={{
          border: `2px solid ${colors.navy}`,
          borderRadius: 1,
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: `${colors.navy} !important`,
            backgroundImage: 'none !important',
            color: '#fff',
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: `${colors.navy} !important`,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.875rem',
            fontFamily: 'inherit',
          },
          '& .MuiDataGrid-columnSeparator': { color: 'rgba(255,255,255,0.35)' },
          // Scoped to the navy header: unscoped, this also whitened the
        // pagination arrows in the white footer, leaving them invisible on any
        // table past 100 rows.
        '& .MuiDataGrid-columnHeaders .MuiSvgIcon-root': { color: '#fff' },
        '& .MuiDataGrid-footerContainer .MuiIconButton-root': { color: colors.blue },
        '& .MuiDataGrid-footerContainer .MuiIconButton-root.Mui-disabled': {
          color: colors.grey[400],
        },
          '& .MuiDataGrid-withBorderColor': { borderColor: `${colors.navy}` },
          '& .MuiDataGrid-cell': { borderBottom: `1px solid ${colors.grey[300]}`, fontSize: '0.875rem' },
          '& .MuiDataGrid-row:hover': { backgroundColor: colors.grey[50] },
          '& .MuiDataGrid-footerContainer': { backgroundColor: colors.grey[50] },
        }}
      />
    </div>
  );
};

export default OperatingExpensesReadOnly; 